import * as path from 'node:path';
import type { BridgeGitRepoContextRow } from '@buildautomaton/types';
import { getBridgeRoot } from '../files/cwd/index.js';
import { yieldToEventLoop } from '../runtime/yield-to-event-loop.js';
import { discoverGitRepos, discoverGitReposUnderRoot } from './discover-repos.js';
import { getCurrentBranch } from './branches/get-current-branch.js';
import { listRepoBranchRefs } from './branches/list-repo-branch-refs.js';
import { isGitRepoDirectory } from './is-git-repo.js';
import { getRemoteOriginUrl } from './remote-origin-url.js';
import type { DiscoveredGitRepo } from './types.js';

function folderNameForRelPath(relPath: string, bridgeRoot: string): string {
  if (relPath === '.' || relPath === '') {
    return path.basename(path.resolve(bridgeRoot)) || 'repo';
  }
  return path.basename(relPath) || relPath;
}

/** When the bridge root is itself a git checkout, only that repo counts (monorepo / single-repo mode). */
async function discoverGitReposForBridgeContext(bridgeRoot: string): Promise<DiscoveredGitRepo[]> {
  const root = path.resolve(bridgeRoot);
  if (await isGitRepoDirectory(root)) {
    const remoteUrl = await getRemoteOriginUrl(root);
    return [{ absolutePath: root, remoteUrl }];
  }

  const [deep, shallow] = await Promise.all([discoverGitReposUnderRoot(root), discoverGitRepos(root)]);
  const byPath = new Map<string, DiscoveredGitRepo>();
  for (const repo of [...deep, ...shallow]) {
    byPath.set(path.resolve(repo.absolutePath), repo);
  }
  return [...byPath.values()];
}

export async function getBridgeGitContext(bridgeRoot: string = getBridgeRoot()): Promise<BridgeGitRepoContextRow[]> {
  const bridgeResolved = path.resolve(bridgeRoot);
  const repos = await discoverGitReposForBridgeContext(bridgeResolved);
  const rows: BridgeGitRepoContextRow[] = [];
  for (let i = 0; i < repos.length; i++) {
    if (i > 0) await yieldToEventLoop();
    const repo = repos[i]!;
    let rel = path.relative(bridgeResolved, repo.absolutePath);
    if (rel.startsWith('..') || path.isAbsolute(rel)) continue;
    const relPath = rel === '' ? '.' : rel.replace(/\\/g, '/');
    const currentBranch = await getCurrentBranch(repo.absolutePath);
    const remoteUrl = repo.remoteUrl.trim() || null;
    rows.push({
      relPath,
      folderName: folderNameForRelPath(relPath, bridgeResolved),
      currentBranch,
      remoteUrl,
    });
  }
  rows.sort((a, b) => a.relPath.localeCompare(b.relPath));
  return rows;
}

export async function listRepoBranchesForBridge(
  repoRelPath: string,
  bridgeRoot: string = getBridgeRoot(),
): Promise<string[]> {
  const bridgeResolved = path.resolve(bridgeRoot);
  const rel = repoRelPath.trim() === '.' ? '' : repoRelPath.trim().replace(/\\/g, '/');
  const repoPath = rel === '' ? bridgeResolved : path.join(bridgeResolved, rel);
  const resolved = path.resolve(repoPath);
  if (!resolved.startsWith(bridgeResolved + path.sep) && resolved !== bridgeResolved) {
    return [];
  }
  return listRepoBranchRefs(resolved);
}
